import { apiClient } from "../../../shared/api/client";
import { generateIdempotencyKey } from "../../../shared/api/idempotency";
import type {
  AbandonTripPayload,
  DeclineOfferPayload,
  AdjustItemPayload,
  AdvanceStopPayload,
  ApprovalSummary,
  ApproveItemPayload,
  CreateTripPayload,
  DeliveryAddress,
  FinalizeTripPayload,
  PickupProofPayload,
  StopMerchantPayload,
  TransitionTripPayload,
  Trip,
  TripDisputePayload,
  TripRatingPayload,
  TripStatus,
  TripType,
  VerifyItemPayload,
} from "../../../entities/trip";

// Service de la COURSE MULTI-ARRÊTS (Trip).
//
// Partagé client + agent : le client CRÉE la course (createTrip), l'agent
// l'EXÉCUTE arrêt par arrêt (advanceTripStop) et la RÈGLE (finalizeTrip).
//
// ⚠️ Argent : createTrip déclenche le hold wallet (120%) côté serveur, et
// finalize/cancel déclenchent capture/release côté serveur. L'app ne doit JAMAIS
// rappeler /wallet/* en parallèle pour une course — tout est géré server-side.

// Seule createTrip porte une Idempotency-Key (POST documenté avec ce header).
// finalizeTrip est money-moving mais le contrat ne déclare PAS le header → on se
// protège du double-règlement au niveau UI (garde synchrone + désactivation).
function idempotencyHeaders(key?: string) {
  return { headers: { "Idempotency-Key": key ?? generateIdempotencyKey() } };
}

// Le helper anti-422 `toStopLocation` (latitude/longitude → lat/lng) vit désormais
// dans `entities/trip` : c'est une transformation PURE, sans réseau. La garder ici
// la rendait intestable sans charger axios + la config d'environnement.
// Ré-exportée pour ne pas casser les consommateurs existants.
export { toStopLocation } from "../../../entities/trip";

// ─── Lectures ─────────────────────────────────────────────────────────────────

export type ListTripsParams = {
  user_id?:  string;
  agent_id?: string;
  status?:   TripStatus;
};

export async function listTrips(params: ListTripsParams = {}): Promise<Trip[]> {
  const { data } = await apiClient.get<Trip[]>("/trips", { params });
  return data;
}

/**
 * Les courses de la Main connectée — offres comprises.
 *
 * `GET /trips` renvoie l'UNION de deux ensembles : les courses attribuées, et
 * celles où une offre est en attente non expirée. C'est la SEULE source de
 * vérité de l'espace agent (cf. `lib/offerPolicy`), et le seul moyen de voir
 * une affectation faite par un administrateur — aucun événement ne la signale.
 *
 * ⚠️ TROIS PIÈGES DE CETTE ROUTE, tous vérifiés côté serveur :
 *
 * 1. **`?agent_id=` est SILENCIEUSEMENT IGNORÉ pour une Main.** L'identité vient
 *    du jeton. On croit filtrer, on ne filtre pas — d'où l'absence délibérée de
 *    ce paramètre ici, pour ne pas entretenir l'illusion.
 *
 * 2. **La pagination renvoyée est décorative** : `page` vaut toujours 1. Le
 *    volume se règle par `limit` (défaut 100, maximum 500).
 *
 * 3. **Un `200` avec `data: []` n'est PAS une preuve d'absence.** C'est aussi ce
 *    que renvoie l'échec de résolution d'identité quand agent-service est
 *    injoignable, et les deux cas sont indiscernables. Ne jamais conclure
 *    « aucune course » sur cette seule base — d'où le libellé prudent des
 *    écrans qui l'affichent.
 */
export async function listMyAgentTrips(limit = 100): Promise<Trip[]> {
  const { data } = await apiClient.get<Trip[]>("/trips", { params: { limit } });
  return data ?? [];
}

export async function getTrip(tripId: string): Promise<Trip> {
  const { data } = await apiClient.get<Trip>(`/trips/${tripId}`);
  return data;
}

// ─── Création (CLIENT) ──────────────────────────────────────────────────────
// Bloque un hold wallet de 120% de estimated_total. 409 = solde insuffisant,
// 422 = validation (stop_index non contigus, items manquants…).

// `idempotencyKey` doit rester STABLE entre les retentatives d'une même création
// (re-tap après timeout) pour que le serveur dédoublonne le hold — l'appelant
// (ClientNewTripScreen) le génère une fois et le réutilise.
export async function createTrip(
  payload: CreateTripPayload,
  idempotencyKey?: string,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>("/trips", payload, idempotencyHeaders(idempotencyKey));
  return data;
}

/**
 * Devis d'une course AVANT création — `POST /trips/estimate`.
 *
 * Renvoie le montant que le serveur bloquera réellement (`hold_amount`, soit
 * 120 % de l'estimation) et dit si le wallet le couvre. C'est la SOURCE DE
 * VÉRITÉ : l'app ne doit plus reconstituer ce chiffre localement.
 *
 * La doc d'intégration en fait « la première cause d'échec de création observée
 * en production » : sans lui, le client validait sur un total puis se voyait
 * refuser au blocage de fonds, sans comprendre l'écart.
 *
 * N'écrit rien, ne réserve rien — pas d'`Idempotency-Key`, l'appel est rejouable.
 *
 * ⚠️ Type de réponse écrit À LA MAIN : l'endpoint n'est pas encore dans
 * l'openapi embarqué (v2.1.0). À remplacer par l'alias généré dès la
 * régénération du contrat.
 */
/**
 * Décomposition du forfait de service, telle que le moteur tarifaire la rend.
 *
 * ## Pourquoi elle compte pour le client
 * Sans elle, `service_fee` est un nombre opaque. L'écran de confirmation
 * l'affichait sous le libellé « Distance », ce qui donnait des lignes
 * intenables : **« Distance · 0 km → 300 F »**. Les 300 F étaient en réalité
 * 100 de prise en charge, 100 de distance et 100 de majoration d'heure de
 * pointe — trois faits que le client ne pouvait ni voir ni contester.
 *
 * `timeRule` est le plus utile des champs : c'est la seule chose qui explique
 * une facture plus élevée à 18 h qu'à 14 h pour le même trajet.
 *
 * ⚠️ Écrit À LA MAIN : l'endpoint n'est pas dans l'openapi embarqué. Le bloc
 * est OPTIONNEL — un serveur plus ancien ne le rend pas, et l'affichage doit
 * retomber sur le forfait global sans rien inventer.
 */
export type TarifDetail = {
  tripType?:            string;
  /** Nombre d'arrêts facturés. */
  stopsCount?:          number;
  /** Distance mesurée du trajet. */
  distanceKm?:          number;
  /** Distance réellement facturée — peut différer de `distanceKm`. */
  billableDistanceKm?:  number;
  /** Prise en charge, par arrêt. */
  baseFeePerStop?:      number;
  /** Prise en charge totale (`baseFeePerStop` × `stopsCount`). */
  baseFee?:             number;
  distanceFee?:         number;
  emergencyFee?:        number;
  /** Majoration horaire. Nulle hors des plages majorées. */
  timeFee?:             number;
  /** Plage horaire appliquée — `PEAK`, `NIGHT`… C'est elle qui NOMME la majoration. */
  timeRule?:            string;
  /** Somme des lignes ci-dessus. Doit égaler `service_fee`. */
  total?:               number;
  currency?:            string;
  /** Version du barème appliqué — utile en cas de contestation. */
  ruleVersion?:         number;
};

export type TripEstimate = {
  /** Total des articles saisis (hors frais). */
  items_estimate:    number;
  /** Frais de service calculés par le serveur. */
  service_fee:       number;
  /** items_estimate + service_fee. */
  estimated_total:   number;
  /** Ce qui sera RÉELLEMENT bloqué sur le wallet (≈ estimated_total × 1.2). */
  hold_amount:       number;
  /** Pourcentage appliqué (120 à ce jour) — configurable côté serveur. */
  hold_percent:      number;
  /** Solde disponible du client. */
  available_balance: number;
  /** Le wallet couvre-t-il `hold_amount` ? */
  sufficient:        boolean;
  /** Ce qui manque quand `sufficient` est faux. */
  missing_amount:    number;

  // ── Champs rendus par le serveur, longtemps ignorés par l'app ────────────
  trip_type?:            string;
  /** Distance mesurée du trajet, côté serveur. */
  distance_km?:          number;
  /** Distance réellement facturée. */
  billable_distance_km?: number;
  /** Le détail du forfait — cf. `TarifDetail`. */
  detail_tarif?:         TarifDetail;
};

export type EstimateTripPayload = {
  distance_km:   number;
  is_emergency?: boolean;
  stops:         { items: { quantity: number; estimated_price?: number }[] }[];
  /**
   * ⚠️ INDISPENSABLE, avec `lat` ET `lng` — §3 du contrat du 2026-08-31.
   *
   * Sans elle, « la course se réduit à son point de retrait : zéro kilomètre,
   * et le devis ne rend que le tarif de base ». Mesuré en production sur un
   * même trajet : **300 F annoncés, 530 F prélevés**. Le client validait donc
   * un montant, et en voyait partir un autre.
   *
   * Le serveur a été corrigé pour en tenir compte ; l'ENVOYER reste de notre
   * côté. C'est la raison d'être de ce champ ici : un devis sans destination
   * n'est pas un devis moins précis, c'est un devis faux.
   */
  delivery_address?: DeliveryAddress;
  /**
   * Nature de la course. Envoyée explicitement, comme à la création : le devis
   * doit chiffrer ce qu'on s'apprête réellement à créer.
   */
  trip_type?:    TripType;
};

export async function estimateTrip(payload: EstimateTripPayload): Promise<TripEstimate> {
  const { data } = await apiClient.post<TripEstimate>("/trips/estimate", payload);
  return data;
}

// ─── Exécution (AGENT) ──────────────────────────────────────────────────────

// Avance UN arrêt dans son cycle (TRAVELING→AT_STOP→PURCHASING→COMPLETED/SKIPPED).
// C'est l'action principale de l'agent : elle pilote automatiquement le statut de
// la course (COMPLETED du dernier arrêt → DELIVERING/DELIVERED).
export async function advanceTripStop(
  tripId:    string,
  stopIndex: number,
  payload:   AdvanceStopPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/stops/${stopIndex}`,
    payload,
  );
  return data;
}

// Transition au niveau course (cas bord / ops). Le chemin nominal agent passe par
// advanceTripStop ; transitionTrip sert surtout à AGENT_EN_ROUTE et aux litiges.
export async function transitionTrip(
  tripId:  string,
  payload: TransitionTripPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/transition`, payload);
  return data;
}

// ─── Bouclier Qualité — vérification AGENT ───────────────────────────────────
// Sur place, l'agent constate la disponibilité réelle de chaque article, prend
// une photo (media_id obtenu via POST /media/upload) et relève le prix réel.
// `available: false` ⇒ article indisponible ; `actual_price` est facultatif
// (un article indisponible n'a pas de prix relevé).
export async function verifyTripItem(
  tripId:    string,
  stopIndex: number,
  itemId:    string,
  payload:   VerifyItemPayload,
): Promise<Trip> {
  const { data } = await apiClient.put<Trip>(
    `/trips/${tripId}/stops/${stopIndex}/items/${itemId}/verify`,
    payload,
  );
  return data;
}

// Une fois tous les articles de l'arrêt vérifiés, l'agent SOUMET l'arrêt à la
// validation du client : la course passe AWAITING_CLIENT_APPROVAL et un délai
// (`client_approval_deadline`) démarre côté serveur.
export async function submitStopForApproval(
  tripId:    string,
  stopIndex: number,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/stops/${stopIndex}/submit-approval`,
    {},
  );
  return data;
}

// ─── Preuve de retrait — LIVRAISON (contrat du 2026-08-26) ───────────────────

/**
 * Photo du colis au point de retrait — `POST .../stops/{i}/pickup-proof`.
 *
 * ## Pourquoi cette route existe
 * Sur une COURSE, la Main achète : on vérifie chaque article, on soumet la
 * liste, le client valide les prix. Une LIVRAISON n'a rien de tout cela — il y
 * a un colis, et il faut savoir que c'est le bon AVANT qu'il parte.
 *
 * ## Enchaînement imposé
 * L'arrivée doit avoir été signalée : l'arrêt doit être `AT_STOP`. Sinon `409`,
 * avec un message qui le dit. La photo passe d'abord par `POST /media/upload`
 * (multipart) ; c'est son identifiant qu'on envoie ici, pas le fichier.
 *
 * ## Ce que ça déclenche
 * La course passe `AWAITING_CLIENT_APPROVAL` et le client reçoit
 * `trip.awaiting_approval` avec `motif: "retrait"` et le `photo_media_id` à
 * afficher. Il valide ensuite par `confirm-approval`, SANS articles à approuver
 * un par un.
 *
 * ⚠️ Sur une COURSE, cette route répond `409` : c'est `submitStopForApproval`
 * qu'il faut. Cf. `lib/livraison.requiresPickupProof`.
 *
 * Réservé à la Main affectée — un superviseur peut le faire en son nom.
 */
export async function submitPickupProof(
  tripId:    string,
  stopIndex: number,
  payload:   PickupProofPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/stops/${stopIndex}/pickup-proof`,
    payload,
  );
  return data;
}

// ─── Bouclier Qualité — décision CLIENT ──────────────────────────────────────
// approve/adjust renvoient un RÉCAP (ApprovalSummary), PAS le Trip directement :
// total approuvé recalculé, frais, nouveau hold et surtout `balance_sufficient`
// (le wallet couvre-t-il encore le total ?) + `deadline` restante. Le Trip à jour
// est dans `summary.trip`.

// Accepter (approved=true) ou rejeter (false) un article vérifié par l'agent.
export async function approveItem(
  tripId:    string,
  stopIndex: number,
  itemId:    string,
  payload:   ApproveItemPayload,
): Promise<ApprovalSummary> {
  const { data } = await apiClient.put<ApprovalSummary>(
    `/trips/${tripId}/stops/${stopIndex}/items/${itemId}/approve`,
    payload,
  );
  return data;
}

// Ajuster la quantité d'un article (0 = retirer l'article de la commande).
export async function adjustItem(
  tripId:    string,
  stopIndex: number,
  itemId:    string,
  payload:   AdjustItemPayload,
): Promise<ApprovalSummary> {
  const { data } = await apiClient.put<ApprovalSummary>(
    `/trips/${tripId}/stops/${stopIndex}/items/${itemId}/adjust`,
    payload,
  );
  return data;
}

// Confirmer la validation de l'arrêt : verrouille les décisions article par
// article et débloque la suite de la course (achats/livraison).
export async function confirmStopApproval(
  tripId:    string,
  stopIndex: number,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/stops/${stopIndex}/confirm-approval`,
    {},
  );
  return data;
}

// Force le serveur à matérialiser l'expiration du délai de validation. Utile
// quand l'app détecte côté client que `client_approval_deadline` est dépassé
// (le serveur tranche : APPROVAL_EXPIRED + cancellation_fee, ou rien si validé
// entre-temps). Idempotent côté serveur.
export async function checkApprovalTimeout(tripId: string): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/check-approval-timeout`,
    {},
  );
  return data;
}

// ─── Règlement / annulation ─────────────────────────────────────────────────

// Capture les fonds et règle toutes les commandes enfants (PAYMENT_SETTLED).
// Le surplus (hold − final) est auto-remboursé au client.
// 409 = course pas DELIVERED ou final > hold.
// 422 = final_amount non calculable (aucun prix final renseigné).
// 503 = (openapi v2.1.0) la capture wallet a échoué, MAIS le backend a programmé
//       une re-tentative automatique (outbox:publish) et la course reste DELIVERED
//       jusqu'au succès. Ne PAS réessayer côté app : finalize n'a pas d'Idempotency-Key
//       au contrat, un retry manuel risquerait une double capture. Cf. AgentTripScreen.
export async function finalizeTrip(
  tripId:   string,
  payload?: FinalizeTripPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/finalize`,
    payload ?? {},
  );
  return data;
}

// Annule la course + commandes enfants ; libère le hold wallet si présent.
//
// ⚠️ RÉSERVÉ AU CLIENT (et à un administrateur). Une Main qui appelle cette
// route reçoit `403` — c'est `abandonTrip` qui lui est destiné.
//
// Depuis le contrat du 2026-08-26, le droit d'annuler s'est INVERSÉ :
//   • OUVERT pendant `AGENT_OFFER_PENDING` — personne n'est encore engagé ;
//   • FERMÉ dès `AGENT_ASSIGNED` → `409`, là où l'app recevait `200`.
// La garde côté écran vit dans `lib/cancelPolicy.canClientCancel`. Le `409`
// porte un message destiné à l'utilisateur : l'afficher tel quel
// (`backendUserMessage`) plutôt que d'en écrire un.
export async function cancelTrip(tripId: string): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/cancel`, {});
  return data;
}

/**
 * La Main REND la course — `POST /trips/{id}/abandon`.
 *
 * ## Le trou que ça bouche
 * Une Main posée sur une course n'avait aucune sortie. Refuser ne vaut que pour
 * une offre en attente, et une Main assignée d'office par un administrateur n'en
 * a jamais eu. Son bouton d'annulation appelait `/cancel`, réservé au client, et
 * recevait `403` en silence — c'est cet appel qui a laissé une Main occupée
 * quatre minutes le 26 août, jusqu'à ce qu'un administrateur intervienne.
 *
 * ## Rendre n'est pas annuler
 * La course repart en recherche, le client garde sa commande, ses fonds restent
 * bloqués. Seul le porteur change. Le retrait est tracé contre la Main, distinct
 * d'un refus d'offre, et la même course ne lui sera pas reproposée.
 *
 * `reason` est obligatoire, de 3 à 255 caractères (cf. `lib/cancelPolicy`).
 *
 * Possible tant que rien n'a été consommé — `AGENT_ASSIGNED` ou
 * `AGENT_EN_ROUTE`. Au-delà, `409` : des achats ont pu être faits, ce n'est
 * plus un retrait mais un litige.
 */
export async function abandonTrip(
  tripId:  string,
  payload: AbandonTripPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/abandon`, payload);
  return data;
}

// ─── Répondre à une offre (MAIN) — contrat du 2026-08-28 ────────────────────

/**
 * Accepter l'offre en cours — `POST /trips/{id}/offer/accept`, corps vide.
 *
 * ## Deux pièges, et ils coûtent cher
 *
 * **Aucune clé d'idempotence n'est acceptée.** Un renvoi après coupure réseau
 * est indiscernable d'un doublon et tombera en `409`. Le remède est en amont —
 * désactiver le bouton dès le premier appui — et en aval : traiter ce `409`
 * comme un succès PROBABLE (recharger, ne pas afficher d'erreur). Cf.
 * `lib/offerPolicy.isProbableDoubleAccept`.
 *
 * **La course rendue est INCOMPLÈTE.** `accept` renvoie les arrêts mais NI les
 * commandes NI les articles, là où `GET /trips/{id}` les inclut. Un écran qui
 * attend la même structure qu'après consultation trouvera `stops[].order` à
 * `null` : il faut recharger par `getTrip` après acceptation.
 *
 * Codes : `409` l'offre n'est plus ouverte · `403` elle est à quelqu'un d'autre
 * (ce n'est PAS une erreur d'authentification) · `410` elle a expiré.
 */
export async function acceptTripOffer(tripId: string): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/offer/accept`, {});
  return data;
}

/**
 * Refuser l'offre — `POST /trips/{id}/offer/decline`.
 *
 * `reason` est obligatoire, de 3 à 255 caractères (`422` sinon).
 *
 * ⚠️ Refuser ne relance PAS cette course : la reprise repart de la plus
 * ancienne en attente, et la relance réelle peut n'arriver qu'une minute plus
 * tard. Ne pas annoncer « elle va être proposée à quelqu'un d'autre ».
 */
export async function declineTripOffer(
  tripId:  string,
  payload: DeclineOfferPayload,
): Promise<void> {
  await apiClient.post(`/trips/${tripId}/offer/decline`, payload);
}

// ─── Assignation (ADMIN/SUPERVISOR) ─────────────────────────────────────────
// Depuis le 2026-08-26, un SECOND appel ne lève plus `409` : la réaffectation
// fonctionne et libère la Main précédente, qui serait autrement restée occupée
// indéfiniment, invisible du vivier. Réaffecter la même Main au même état est
// sans effet et renvoie `200` — un double clic n'est pas une erreur.
// (Cette app n'a pas d'espace d'administration ; la fonction reste exposée pour
// les outils internes qui l'utilisent.)
export async function assignTripAgent(tripId: string, agentId: string): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/assign`, { agent_id: agentId });
  return data;
}


// ─── Relevé de la boutique (AGENT) ──────────────────────────────────────────

/**
 * Nom et numéro du vendeur — `POST /trips/{id}/stops/{n}/merchant`.
 *
 * §4 du contrat du 2026-08-31, étape 3 du déroulé d'une course avec achats :
 * elle se place APRÈS l'arrivée à l'arrêt (`AT_STOP`) et AVANT la photo des
 * articles.
 *
 * ## Pourquoi c'est la Main qui relève
 * « Le client ne connaît pas toujours le numéro du vendeur. » C'est pourtant la
 * DESTINATION du virement : depuis le 2026-09-04 la plateforme décaisse
 * elle-même vers lui, depuis son compte chez l'agrégateur. Sans ce relevé, la
 * file de versement n'a personne à payer, et la Main reste devant le comptoir.
 *
 * ## Le risque assumé, et ce que l'écran doit en dire
 * La provenance est conservée (`phone_source: AGENT`) et trois signaux de
 * fraude sont calculés dessus, dont « le numéro déclaré est celui de la main ».
 * Le contrat le formule ainsi : « Déclarer son propre numéro est détecté. »
 *
 * Ce n'est pas une menace à relayer telle quelle, mais l'écran doit être clair
 * sur ce qu'on demande — le numéro DU VENDEUR — car une Main pressée qui
 * donnerait le sien de bonne foi lèverait une alerte rouge sur son dossier.
 *
 * Réservé à la Main posée sur la course, ou à un opérateur en son nom.
 */
export async function setStopMerchant(
  tripId:    string,
  stopIndex: number,
  payload:   StopMerchantPayload,
): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(
    `/trips/${tripId}/stops/${stopIndex}/merchant`,
    payload,
  );
  return data;
}

// ─── Note et litige (CLIENT) ────────────────────────────────────────────────

/**
 * Note d'une course — `POST /trips/{id}/rating`.
 *
 * Une course, une note. Ouvert dès `DELIVERED`, sans attendre le règlement :
 * cf. `lib/feedbackPolicy.canRateTrip` pour la fenêtre exacte et ses raisons.
 *
 * La note alimente une moyenne pondérée réelle. Jusqu'ici aucune n'était posée,
 * et « un quart du classement des mains reposait sur une constante » — 4,5 pour
 * tout le monde.
 */
export async function rateTrip(tripId: string, payload: TripRatingPayload): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/rating`, payload);
  return data;
}

/**
 * Ouverture d'un litige — `POST /trips/{id}/dispute`.
 *
 * Ouvrable de `DELIVERING` à `PAYMENT_SETTLED`. Le motif fait au moins dix
 * caractères — « un litige sans explication ne s'arbitre pas » —, à faire
 * respecter DANS le formulaire plutôt qu'au retour : un `422` après envoi fait
 * perdre au client le texte qu'il vient d'écrire.
 *
 * ⚠️ `409` sur un SECOND litige. Ce n'est pas une panne : le premier suit son
 * cours. L'écran doit montrer le litige existant, pas proposer de réessayer.
 *
 * La suite appartient au back-office. Le client n'a rien à interroger : la
 * décision lui revient par la salle de la course, en `trip.refunded` ou
 * `trip.dispute.rejected`.
 */
export async function disputeTrip(tripId: string, payload: TripDisputePayload): Promise<Trip> {
  const { data } = await apiClient.post<Trip>(`/trips/${tripId}/dispute`, payload);
  return data;
}

// ─── Compléter la provision (CLIENT) ────────────────────────────────────────

/** Ce que le serveur rend après avoir agrandi la provision. */
export type ProvisionComplement = {
  trip_id?:     string;
  /** Montant ajouté à la provision, calculé par le SERVEUR. */
  complement?:  number;
  /** Nouvelle provision totale. */
  hold_amount?: number;
};

/**
 * Agrandit la provision d'une course — `POST /trips/{id}/complement`.
 *
 * §1 du document du 2026-09-05, et c'est une réparation, pas un confort.
 *
 * ## Ce qu'il évite
 * Une course estimée 100 F gèle 120 F. Si l'article vaut 125 F au marché, il
 * manque 5 F et la confirmation est refusée. Le client n'avait alors que deux
 * issues — renoncer à l'article, ou laisser expirer — et **les deux coûtent le
 * forfait de service entier, sans marchandise**. Le 5 septembre : 200 F
 * prélevés pour 5 F de dépassement, avec 620 F disponibles.
 *
 * ## AUCUN CORPS, et c'est le point important
 * Le serveur calcule lui-même ce qui manque, vérifie que le solde le couvre, et
 * agrandit la provision. « Le client n'indique aucun montant : on ne lui demande
 * pas de calculer, on lui demande d'accepter. »
 *
 * Envoyer un montant depuis l'app serait doublement fautif — on recalculerait un
 * chiffre que le serveur détient, et un écart d'un franc ferait échouer une
 * opération d'argent.
 *
 * ## Les refus, tous en `409` sauf deux
 * « La provision suffit deja », « Solde insuffisant », « Trip is not awaiting
 * client approval » ; plus un `410` si le délai est passé et un `502` si le
 * portefeuille a flanché. Ils portent tous leur message : on l'affiche tel quel.
 *
 * ⚠️ Ne JAMAIS appeler `POST /wallet/hold/extend` : le portefeuille n'est pas
 * exposé par la passerelle publique, et c'est order-service qui s'en sert
 * derrière cette route (§2).
 */
export async function completeTripProvision(tripId: string): Promise<ProvisionComplement> {
  const { data } = await apiClient.post<ProvisionComplement>(
    `/trips/${tripId}/complement`,
    // Aucun corps : le montant est l'affaire du serveur.
    {},
  );
  return data;
}
